import * as React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

import defaultStyles from '../../styles/formSetStyles.module.css';
import styles from './index.module.css';

import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import * as api from '../../api';

import { Button, Margin } from '../Button';
import { GroupInputs } from '../GroupInputs';
import { isEnumMember } from 'common/dist/utils/types';
import LabeledInput from '../LabeledInput';
import { ModalInput } from '../ModalInput';
import { MultiEnum } from './MultiEnum';
import { SelectInput } from '../SelectInput';
import { getOptionsFromRecord, getOptionsIntersection } from '../utils';

interface Props extends WithTranslation {
  substanceNameOptions: api.SubstanceUsage.SubstanceName[];
  substanceUsageStatuses: api.SubstanceUsage.Status[];
  substanceUsageFrequencies: api.Previsit.Frequency[];
  saveFieldValue: (value: string) => void;
  updateFieldValue: (value: string) => void;
  value?: string;
}

interface State {
  substanceUsage: Partial<api.SubstanceUsage.PrevisitSubstanceUsage>;
  isModalOpen: boolean;
}

const usageSelectStatusValues = getOptionsFromRecord(
  api.SubstanceUsage.statusMap,
);

class SubstanceUsage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    try {
      const substanceUsage: api.SubstanceUsage.PrevisitSubstanceUsage =
        props.value && JSON.parse(props.value);

      const filteredSubstance = {
        ...substanceUsage,
        status:
          substanceUsage.status &&
          !this.props.substanceUsageStatuses.includes(substanceUsage.status)
            ? undefined
            : substanceUsage.status,
      };
      this.state = {
        substanceUsage: filteredSubstance || {
          status: '',
          frequency: '',
          substances: [],
        },
        isModalOpen: false,
      };
      if (
        substanceUsage &&
        substanceUsage.status &&
        !this.props.substanceUsageStatuses.includes(substanceUsage.status)
      ) {
        this.updateFormField(filteredSubstance);
      }
    } catch (e) {}
  }

  updateFormField = (
    updatedSubstanceUsage: Partial<api.SubstanceUsage.PrevisitSubstanceUsage>,
  ) => {
    const { updateFieldValue, saveFieldValue } = this.props;
    updateFieldValue(JSON.stringify(updatedSubstanceUsage));
    saveFieldValue(JSON.stringify(updatedSubstanceUsage));
  };

  handleSelect = (newValue: api.SubstanceUsage.SubstanceName) => {
    const { substanceUsage } = this.state;
    const { substances } = substanceUsage;

    let updatedSubstanceUsage: Partial<api.SubstanceUsage.PrevisitSubstanceUsage>;
    if (!substances) {
      updatedSubstanceUsage = {
        ...substanceUsage,
        substances: [{ name: newValue }],
      };
    } else {
      const needsRemove = substances.some(
        (substance) => newValue === substance.name,
      );
      updatedSubstanceUsage = {
        ...substanceUsage,
        substances: needsRemove
          ? substances.filter((substance) => newValue !== substance.name)
          : [...substances, { name: newValue }],
      };
    }
    this.setState({ substanceUsage: updatedSubstanceUsage });
    this.updateFormField(updatedSubstanceUsage);
  };

  modalContent = (status: api.SubstanceUsage.Status) => {
    const { substanceUsage } = this.state;
    const { t } = this.props;
    const { substances } = substanceUsage;

    const verbTense =
      (status === api.SubstanceUsage.Status.CurrentlyUse && 'do') ||
      (status === api.SubstanceUsage.Status.FormerlyUsed && 'did') ||
      '';

    const label = t(
      `Which substances ${verbTense} you use? Select all that apply.`,
    );

    const substanceOptions = getOptionsIntersection(
      api.SubstanceUsage.SubstanceName,
      this.props.substanceNameOptions,
    ).map((sub) => {
      return {
        display: t(api.SubstanceUsage.substanceNameMap[sub]),
        key: sub,
        value: sub,
      };
    });

    return (
      <>
        <div className={defaultStyles.Label}>{label}</div>
        <MultiEnum
          values={
            (substances && substances.map((substance) => substance.name)) || []
          }
          options={substanceOptions}
          onItemClick={this.handleSelect}
        />
        <Button
          type="button"
          children={t('Continue')}
          onClick={() => this.setIsModalOpen(false)}
          margin={Margin.Enum}
        />
      </>
    );
  };

  setIsModalOpen = (newState: boolean) => {
    this.setState({ isModalOpen: newState });
  };

  updateSubstanceUsage = (
    field: string,
    newValue: string | api.SubstanceUsage.SubstanceName[] | boolean,
  ) => {
    const { substanceUsage } = this.state;
    const shouldNotHaveFrequency =
      field === 'status' &&
      [
        api.SubstanceUsage.Status.NeverUsed,
        api.SubstanceUsage.Status.UnableToObtain,
        api.SubstanceUsage.Status.PreferNotToAnswer,
      ].some((status) => status === newValue);
    const newSubstanceUsage = shouldNotHaveFrequency
      ? {
          ...substanceUsage,
          status: newValue as api.SubstanceUsage.Status,
          frequency: undefined,
          substances: undefined,
        }
      : { ...substanceUsage, ...{ [field]: newValue } };
    this.setState({ substanceUsage: newSubstanceUsage });
    this.updateFormField(newSubstanceUsage);
  };

  updateFrequency = (update: {
    frequency: api.Previsit.Frequency;
    frequencyRaw: string;
  }) => {
    const { substanceUsage } = this.state;
    const newFrequency = { ...substanceUsage, ...update };
    this.setState({ substanceUsage: newFrequency });
    this.updateFormField(newFrequency);
  };

  render() {
    const { t } = this.props;
    const statusEnum = api.SubstanceUsage.Status;
    const frequencyMap = api.Previsit.FrequencyMap;

    const { substanceUsage, isModalOpen } = this.state;
    const { status, frequency, substances, concernsAtHome } = substanceUsage;

    const yesNoSelectValues = [
      {
        display: t('Yes'),
        key: 'yes',
        value: 'true',
      },
      { display: t('No'), key: 'no', value: 'false' },
    ];

    const presentEnums = [
      statusEnum.CurrentlyUse,
      statusEnum.InRecovery,
      statusEnum.Other,
    ];
    const pastEnums = [
      statusEnum.FormerlyUsed,
      statusEnum.InEarlyPregnancy,
      statusEnum.NotSincePregnant,
    ];

    const drugsUsedLabel =
      (status && presentEnums.includes(status) && 'Which drugs do you use?') ||
      (status && pastEnums.includes(status) && 'Which drugs did you use?') ||
      '';

    const frequencyLabel =
      (status &&
        presentEnums.includes(status) &&
        'How often do you typically use these drugs?') ||
      (status &&
        pastEnums.includes(status) &&
        'When you used to use these drugs, how often did you typically use them?') ||
      '';

    const frequencyOptions = getOptionsIntersection(
      api.Previsit.Frequency,
      this.props.substanceUsageFrequencies,
    ).map((freq) => {
      return {
        display: t(frequencyMap[freq]),
        key: freq,
        value: freq,
      };
    });

    return (
      <div className={styles.SubstanceUsage}>
        <GroupInputs>
          <LabeledInput label={t('Status')} htmlFor="substance-usage-status">
            <SelectInput
              id="substance-usage-status"
              value={status || ''}
              onChange={(newValue: string) =>
                this.updateSubstanceUsage('status', newValue)
              }
              options={usageSelectStatusValues.filter((option) => {
                return this.props.substanceUsageStatuses.includes(
                  option.value as api.SubstanceUsage.Status,
                );
              })}
            />
          </LabeledInput>
          {status &&
            [
              api.SubstanceUsage.Status.CurrentlyUse,
              api.SubstanceUsage.Status.FormerlyUsed,
              api.SubstanceUsage.Status.InEarlyPregnancy,
              api.SubstanceUsage.Status.NotSincePregnant,
              api.SubstanceUsage.Status.InRecovery,
              api.SubstanceUsage.Status.Other,
            ].includes(status) && (
              <>
                <LabeledInput label={t(drugsUsedLabel)}>
                  <ModalInput
                    children={this.modalContent(status)}
                    isOpen={isModalOpen}
                    setIsOpen={this.setIsModalOpen}
                    placeholder={t('Select an option')}
                    endIcon={
                      <ArrowDropDownIcon className={styles.ArrowDropDownIcon} />
                    }
                    onModalClose={() => this.setIsModalOpen(false)}
                    initialValue={
                      (substances &&
                        substances
                          .map(({ name }) =>
                            t(api.SubstanceUsage.substanceNameMap[name]),
                          )
                          .sort()
                          .join(', ')) ||
                      ''
                    }
                  />
                </LabeledInput>
                <LabeledInput label={t(frequencyLabel)}>
                  <SelectInput
                    value={frequency || ''}
                    onChange={(newValue: string) => {
                      if (!isEnumMember(api.Previsit.Frequency, newValue)) {
                        return;
                      }
                      this.updateFrequency({
                        frequency: newValue,
                        frequencyRaw: frequencyMap[newValue],
                      });
                    }}
                    options={frequencyOptions}
                  />
                </LabeledInput>
              </>
            )}
          <LabeledInput
            label={t('Are you concerned about substance use at home?')}
            htmlFor="substance-usage-concerned"
          >
            <SelectInput
              id="substance-usage-concerned"
              value={concernsAtHome !== undefined ? `${concernsAtHome}` : ''}
              onChange={(newValue: string) => {
                this.updateSubstanceUsage(
                  'concernsAtHome',
                  newValue === 'true',
                );
              }}
              options={yesNoSelectValues}
            />
          </LabeledInput>
        </GroupInputs>
      </div>
    );
  }
}

export default withTranslation()(SubstanceUsage);
